import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { TableNode } from "@lexical/table";
import { Box } from "@mui/material";
import { type FC, useMemo } from "react";
import { HandleSize } from "../../components/image-tools";
import { getTableColumnWidths } from "../../utils/get-table-column-widths";
import type { BoundaryConditions, DynamicConditions } from "../table-menu-plugin";

export const ResizeHandles: FC<{
  boundaryConditions: BoundaryConditions;
  tableNode: TableNode;
  dynamicConditions: DynamicConditions;
  handlePointerDown: (
    event: React.PointerEvent<HTMLDivElement>,
    movingPillarIndex: number,
    startColumnWidths: readonly number[],
    movingHandleIndex: number,
  ) => void;
  currentlyInteractingPillarIndex: null | number;
}> = ({
  boundaryConditions,
  tableNode,
  handlePointerDown,
  dynamicConditions,
  currentlyInteractingPillarIndex,
}) => {
  const [editor] = useLexicalComposerContext();

  const handles = useMemo(
    () =>
      boundaryConditions.handles.map(({ pillarIndex }, index) => ({
        pillarIndex,
        ...dynamicConditions.handles[index],
        isCurrentlyResizing: currentlyInteractingPillarIndex === pillarIndex,
      })),
    [boundaryConditions, dynamicConditions, currentlyInteractingPillarIndex],
  );

  return (
    <>
      {handles.map(({ pillarIndex, leftPercent, isCurrentlyResizing }, handleIndex) => (
        <Box
          key={`column-resize-handle-${pillarIndex}`}
          data-analytics-id={`column-resize-handle`}
          className="table-cell-resize-button"
          sx={{
            pointerEvents: "auto",
            position: "absolute",
            width: `${HandleSize * 2}px`,
            height: `${HandleSize * 2}px`,
            top: "50%",
            left: `${leftPercent}%`,
            transform: "translate(-50%, -50%)",
            cursor: `ew-resize`,
          }}
          onPointerDown={(event) => {
            let columnWidths: number[] = [];
            editor.read(() => {
              columnWidths = getTableColumnWidths(tableNode);
            });
            handlePointerDown(event, pillarIndex, columnWidths, handleIndex);
          }}
          data-testid={`column-resize-handle-${pillarIndex}`}
        >
          <Box
            sx={(theme) => ({
              position: "absolute",
              background: isCurrentlyResizing
                ? theme.palette.primary.light
                : theme.palette.neutral[100],
              border: `2px solid ${theme.palette.info.main}`,
              width: `${HandleSize}px`,
              height: `${HandleSize}px`,
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
            })}
          ></Box>
        </Box>
      ))}
    </>
  );
};
